import { Suspense, useEffect, useRef } from 'react';
import ReactGA from 'react-ga';
import { Redirect, Switch, useHistory, useLocation } from 'react-router-dom';
import * as Banner from '@givebutter/primitives/banner';
import { Loader } from '@givebutter/primitives/loader';
import Content from 'assets/js/components/Content';
import { PaymentMethodIssueBanner } from 'assets/js/components/PaymentMethodIssueBanner';
import { ScrollTop } from 'assets/js/components/ScrollTop';
import NotFound from 'assets/js/pages/NotFound';
import AccountSettings from 'assets/js/pages/account-settings';
import { VerificationSettings } from 'assets/js/pages/account-settings/account-about/VerificationSettings';
import CampaignSettings from 'assets/js/pages/campaign-settings/CampaignSettingsRouter';
import Campaigns from 'assets/js/pages/campaigns/Campaigns';
import Chapters from 'assets/js/pages/chapters/Chapters';
import { ContactsRouter } from 'assets/js/pages/contacts/ContactsRouter';
import { CompaniesRouter } from 'assets/js/pages/contacts/companies/CompaniesRouter';
import { DataHygieneRouter } from 'assets/js/pages/contacts/data-hygiene/DataHygieneRouter';
import { DuplicatesRouter } from 'assets/js/pages/contacts/duplicates/DuplicatesRouter';
import EngageRouter from 'assets/js/pages/engage/EngageRouter';
import { WorkflowsRouter } from 'assets/js/pages/engage/WorkflowsRouter';
import { Payouts } from 'assets/js/pages/payouts/Payouts';
import { PledgesRouter } from 'assets/js/pages/pledges/PledgesRouter';
import { RecurringPlansRouter } from 'assets/js/pages/recurring-payments/RecurringPlansRouter';
import { ReportsRouter } from 'assets/js/pages/reports/ReportsRouter';
import TransactionsRouter from 'assets/js/pages/transactions/TransactionsRouter';
import { ConnectPayoutMethodBanner } from 'components/connect-payout-method-banner';
import { PermissionErrorBoundary } from 'components/error-boundaries/PermissionErrorBoundary';
import { RateLimitErrorBoundary } from 'components/error-boundaries/RateLimitErrorBoundary';
import { DataHygieneGivebutterPlusSplashPage } from 'components/givebutter-plus-splash/DataHygieneGivebutterPlusSplash';
import { TasksGivebutterPlusSplashPage } from 'components/givebutter-plus-splash/TasksGivebutterPlusSplashPage';
import { Header } from 'components/header';
import { TicketsRouter } from 'components/pages/tickets/TicketsRouter';
import { RequiredFloatingChecklist as RequiredFloatingChecklistComponent } from 'components/required-checklist';
import { useRequiredChecklistVisibility } from 'components/required-checklist/useRequiredChecklistVisibility';
import { Route } from 'components/router/Route';
import { Sidebar } from 'components/sidebar';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useRedirectUnverifiedUser } from 'customHooks/useRedirectUnverifiedUser';
import { useScrollToHashId } from 'customHooks/useScrollToHashId';
import { Location } from 'history';
import { Settings } from 'luxon';
import Home from 'pages/home/Home';
import { LaunchDarklyWatcher } from 'providers/LaunchDarklyProvider';
import { twJoin } from 'tailwind-merge';

const Pages = () => {
    const history = useHistory();
    const location = useLocation();
    const didInitAnalytics = useRef(false);

    useRedirectUnverifiedUser();

    useEffect(() => {
        // we only want this to run once, on initial page load
        if (!didInitAnalytics.current) {
            ReactGA.initialize(import.meta.env.VITE_DASHBOARD_GOOGLE_ANALYTICS_ID || '');
            const setAnalytics = (location: Location) => {
                ReactGA.set({
                    page: location.pathname,
                    title: location.pathname,
                });

                ReactGA.pageview(window.location.pathname + window.location.search);
            };

            history.listen(location => {
                setAnalytics(location);
                window._sift?.push(['_trackPageview']);
            });

            setAnalytics(location);
        }
        didInitAnalytics.current = true;
    }, [history, location]);

    return (
        <Banner.Provider>
            <section className="flex min-h-0 flex-col bg-gray-100">
                <Header />
                <LayoutContent />
                <Sidebar />
            </section>
        </Banner.Provider>
    );
};

const LayoutContent = () => {
    const contentRef = useRef<HTMLElement>(null);
    useScrollToHashId();

    return (
        <main
            ref={contentRef}
            className={twJoin(
                '2xl:ml-76 relative ml-0 flex min-h-screen flex-col bg-white p-4 pt-24 text-base font-normal transition-none duration-200 sm:px-6 md:px-8 lg:ml-64',
                'print:!ml-0 print:block print:h-full print:w-full print:overflow-y-hidden',
            )}
        >
            <Banner.Viewport className="z-pentOutermost sticky top-16 -mx-4 -mt-8 mb-4 sm:-m-8 sm:mb-8" />

            <Suspense fallback={null}>
                <ConnectPayoutMethodBanner />
                <PaymentMethodIssueBanner />
            </Suspense>

            <ScrollTop>
                <RateLimitErrorBoundary>
                    <PermissionErrorBoundary>
                        <Suspense fallback={<PagesFallback />}>
                            <RequiredFloatingChecklist />
                            <AccountDefaultTimezone />
                            <LaunchDarklyWatcher>
                                <Switch>
                                    <Route
                                        exact
                                        path={[
                                            '/',
                                            '/accounts/:account_id',
                                            '/accounts/:account_id/home',
                                        ]}
                                        component={HomeRoute}
                                    />
                                    <Route
                                        component={ContactsRouter}
                                        path={'/accounts/:account_id/contacts'}
                                    />
                                    <Route
                                        component={CompaniesRouter}
                                        path={'/accounts/:account_id/companies'}
                                    />
                                    <Route
                                        component={DuplicatesRouter}
                                        path={'/accounts/:account_id/duplicates'}
                                    />
                                    <Route
                                        path={`/accounts/:account_id/data-hygiene`}
                                        component={DataHygieneRouter}
                                    />
                                    <Route
                                        path={`/accounts/:account_id/upgrade`}
                                        component={DataHygieneGivebutterPlusSplashPage}
                                    />
                                    <Route
                                        exact
                                        component={VerificationSettings}
                                        path="/accounts/:account_id/verify"
                                    />
                                    <Route
                                        exact
                                        component={Campaigns}
                                        path="/accounts/:account_id/campaigns"
                                    />
                                    <Route
                                        component={CampaignSettings}
                                        path="/accounts/:account_id/campaigns/:campaign_id"
                                    />
                                    <Route
                                        component={TransactionsRouter}
                                        path="/accounts/:account_id/transactions"
                                    />
                                    <Route
                                        component={RecurringPlansRouter}
                                        path="/accounts/:account_id/plans"
                                    />
                                    <Route
                                        component={PledgesRouter}
                                        path="/accounts/:account_id/pledges"
                                    />
                                    <Route
                                        path="/accounts/:account_id/tickets"
                                        component={TicketsRouter}
                                    />
                                    <Route
                                        path="/accounts/:account_id/payouts"
                                        component={Payouts}
                                    />
                                    <Route
                                        component={AccountSettings}
                                        path="/accounts/:account_id/settings"
                                    />
                                    <Route
                                        exact
                                        component={Chapters}
                                        path="/accounts/:account_id/chapters"
                                    />
                                    <Route
                                        component={EngageRouter}
                                        path="/accounts/:account_id/engage"
                                    />
                                    <Route
                                        component={ReportsRouter}
                                        path="/accounts/:account_id/reports"
                                    />
                                    <Route
                                        path="/accounts/:account_id/tasks"
                                        component={WrappedTasksSplash}
                                    />
                                    <Route
                                        path={`/accounts/:account_id/workflows`}
                                        component={WorkflowsRouter}
                                    />
                                    <Route component={NotFound} />
                                </Switch>
                            </LaunchDarklyWatcher>
                        </Suspense>
                    </PermissionErrorBoundary>
                </RateLimitErrorBoundary>
            </ScrollTop>
        </main>
    );
};

const WrappedTasksSplash = () => {
    const { hasGivebutterPlusAccess } = useCurrentAccount();

    return !hasGivebutterPlusAccess ? <TasksGivebutterPlusSplashPage /> : <NotFound />;
};

const HomeRoute = () => {
    const location = useLocation();
    const { accountId } = useCurrentAccount();

    if (location.pathname === '/') {
        return <Redirect to={`/accounts/${accountId}/home`} />;
    }

    return <Home />;
};

const PagesFallback = () => {
    return (
        <Content className={'relative min-h-72'}>
            <Loader />
        </Content>
    );
};

const AccountDefaultTimezone = () => {
    const { timezone } = useCurrentAccount();

    useEffect(() => {
        if (timezone) {
            Settings.defaultZone = timezone;
        }
    }, [timezone]);

    return null;
};

const RequiredFloatingChecklist = () => {
    const { shouldShowChecklists } = useRequiredChecklistVisibility();

    return <>{shouldShowChecklists && <RequiredFloatingChecklistComponent />}</>;
};

export { Pages };
